import React, { useState } from "react";
import { LumenExpandIcon } from "../../lib/Icons/LumenIcon.js";
import { SessionRail, SessionSidebar, SessionTag, sessionTitle } from "./LumenSessionList";
import { IconLink } from "../../lib/Icons/SessionIcons";

const TEAL = "#4DB8B5";
const FONT = "'DM Sans', sans-serif";

function Chevron({ up }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ transform: up ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function Identity({ subtitle, thinking }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      <div style={{
        width: 34, height: 34, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center",
        justifyContent: "center", fontSize: 14, color: TEAL,
        background: thinking ? "rgba(77,184,181,0.22)" : "rgba(77,184,181,0.1)",
        border: "1px solid rgba(77,184,181,0.3)", transition: "background 0.2s",
      }}>✦</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B", fontFamily: FONT, letterSpacing: "0.04em" }}>LUMEN</div>
        <div style={{ fontSize: 11, color: "#64748B", fontFamily: FONT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function SessionHeader({ roomName, session, thinking }) {
  return (
    <div data-lumen-session-header style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, marginRight: "auto", paddingLeft: 4 }}>
      <div style={{
        width: 30, height: 30, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center",
        justifyContent: "center", fontSize: 13, color: TEAL,
        background: thinking ? "rgba(77,184,181,0.22)" : "rgba(77,184,181,0.1)",
        border: "1px solid rgba(77,184,181,0.3)",
      }}>✦</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "#1E293B", fontFamily: FONT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          <span style={{ fontWeight: 700, letterSpacing: "0.04em" }}>LUMEN</span>
          {roomName && <span style={{ color: "#475569" }}> {roomName}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span style={{ fontSize: 12, color: "#475569", fontFamily: FONT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {sessionTitle(session)}
          </span>
          {session && <SessionTag surface={session.surface} />}
        </div>
      </div>
    </div>
  );
}

function RelatedStrip({ sessions }) {
  if (!sessions?.activeId || !sessions.relatedOf || sessions.mode === "building") return null;
  const byId = new Map((sessions.items || []).map((item) => [item.id, item]));
  const related = sessions.relatedOf(sessions.activeId).map((id) => byId.get(id)).filter(Boolean);
  if (related.length === 0) return null;
  return (
    <div data-lumen-related style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, padding: "8px 12px 0", fontFamily: FONT }}>
      <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#64748B" }}>
        <IconLink size={13} /> Related chats:
      </span>
      {related.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => sessions.onOpen(item.id)}
          style={{
            padding: "3px 9px", borderRadius: 999, border: "1px solid rgba(77,184,181,0.35)", background: "rgba(77,184,181,0.08)",
            color: "#2F8C89", fontSize: 12, fontWeight: 600, fontFamily: FONT, cursor: "pointer", maxWidth: 220,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {sessionTitle(item)}
        </button>
      ))}
    </div>
  );
}

const iconButton = {
  background: "none", border: "none", color: "#475569", cursor: "pointer",
  padding: 4, lineHeight: 1, display: "flex", alignItems: "center", borderRadius: 6,
};

export default function LumenDockLayout({
  dock, roomName, showThinking, onBrainDump, onMaximize,
  messagesArea, worldChips, onChip, inputForm,
}) {
  const [sessionsOpen, setSessionsOpen] = useState(true);
  const subtitle = dock.subtitle || roomName || "My World";
  const card = {
    width: "100%", height: "100%", boxSizing: "border-box", display: "flex",
    // Slightly see-through so the world still shows behind the dock.
    background: "rgba(255,255,255,0.84)", backdropFilter: "blur(14px) saturate(140%)", WebkitBackdropFilter: "blur(14px) saturate(140%)",
    border: "1px solid rgba(0,0,0,0.08)", borderRadius: 16,
    boxShadow: "0 8px 28px rgba(0,0,0,0.14)", overflow: "hidden",
  };

  if (dock.collapsed) {
    return (
      <div data-lumen-dock="collapsed" style={{ ...card, alignItems: "center", gap: 14, padding: "0 14px" }}>
        <div style={{ width: 170, flexShrink: 0 }}>
          <Identity subtitle={subtitle} thinking={showThinking} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }} onSubmitCapture={() => dock.onExpand?.()}>
          {inputForm}
        </div>
        <button type="button" onClick={dock.onExpand} title="Open Lumen" style={iconButton}>
          <Chevron up />
        </button>
      </div>
    );
  }

  const chips = dock.chips || [];
  const sessions = dock.sessions || null;
  const showRail = sessions && !sessionsOpen;
  const activeSession = sessions?.items?.find((item) => item.id === sessions.activeId) || null;

  const chipRow = chips.length > 0 ? (
    <div data-lumen-chips style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "0 12px 4px", flexShrink: 0 }}>
      {chips.map((chip) => {
        const Icon = chip.Icon;
        return (
          <button
            key={chip.id}
            type="button"
            title={chip.hint || chip.label}
            onClick={() => onChip(chip)}
            style={{
              display: "flex", alignItems: "center", gap: 8, textAlign: "left",
              padding: "8px 12px", borderRadius: 10, background: "rgba(255,255,255,0.72)", cursor: "pointer",
              border: "1px solid rgba(0,0,0,0.08)", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              color: "#334155", fontSize: 12.5, fontWeight: 600, fontFamily: FONT,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(77,184,181,0.5)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.08)"; }}
          >
            {Icon && <span style={{ color: TEAL, display: "flex" }}><Icon size={16} /></span>}
            <span>{chip.label}</span>
          </button>
        );
      })}
    </div>
  ) : worldChips ? (
    <div data-lumen-chips style={{ padding: "0 12px 4px", flexShrink: 0 }}>{worldChips}</div>
  ) : null;

  return (
    <div data-lumen-dock="expanded" style={card}>
      <div style={{
        width: showRail ? 56 : 220, flexShrink: 0, padding: showRail ? "12px 0" : 14, boxSizing: "border-box",
        display: "flex", flexDirection: "column", gap: 12, borderRight: "1px solid rgba(0,0,0,0.06)",
        overflow: sessions ? "hidden" : "auto",
      }}>
        {!sessions ? (
          <Identity subtitle={subtitle} thinking={showThinking} />
        ) : showRail ? (
          <SessionRail sessions={sessions} onShow={() => setSessionsOpen(true)} />
        ) : (
          <SessionSidebar
            identity={<Identity subtitle={subtitle} thinking={showThinking} />}
            sessions={sessions}
            onHide={() => setSessionsOpen(false)}
          />
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, padding: showRail ? "10px 10px 8px" : "6px 10px 0", borderBottom: showRail ? "1px solid rgba(0,0,0,0.06)" : "none" }}>
          {showRail && <SessionHeader roomName={roomName || subtitle} session={activeSession} thinking={showThinking} />}
          {showThinking && <div style={{ fontSize: 16, color: TEAL, letterSpacing: 2, marginRight: showRail ? 0 : "auto", paddingLeft: 4 }}>···</div>}
          {onBrainDump && (
            <button type="button" onClick={onBrainDump}
              style={{ padding: "3px 8px", borderRadius: 6, background: "rgba(77,184,181,0.08)", border: "1px solid rgba(77,184,181,0.2)", color: TEAL, fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" }}>
              ⚡ Brain Dump
            </button>
          )}
          {onMaximize && (
            <button type="button" onClick={onMaximize} title="Expand Lumen" style={iconButton}>
              <LumenExpandIcon className="transition-transform duration-300 rotate-180" />
            </button>
          )}
          <button type="button" onClick={dock.onCollapse} title="Collapse Lumen" style={iconButton}>
            <Chevron />
          </button>
        </div>
        <RelatedStrip sessions={sessions} />
        {messagesArea}
        {chipRow}
        {inputForm}
      </div>
    </div>
  );
}
